import type {
  Database,
  Company,
  User,
  Project,
  ContractItem,
  InterimApplication,
  Tender,
  RetentionLedger,
  Task,
  TaskComment,
  Notification,
  ShareToken,
  RateLibrary,
  MainContractor,
  Contact,
  EstimatingTeamMember,
  Supplier,
  TermsTemplate,
  RateLibraryItem,
  RICSBenchmark,
  CommercialHealthMetric,
  Employer,
  ClientEmployerRelationship,
  EntitlementAlert,
  ActiveDispute,
  ClientRequest,
  PendingDocumentUpload,
} from '../../../types/database';
import type { Claim, ClaimCorrespondence, ClaimSummaryMetrics, ClaimFilters } from '../../../types/claims';
import type { Document } from '../../../types/documents';
import type {
  Activity,
  ActivityFilter,
  ActivityStats,
  CreatePhoneCallData,
  CreateMeetingData,
  CreateEmailData,
  CreateNoteData,
  CreateCommentData,
} from '../../../types/activity';
import { ProjectFoldersService } from '../../ProjectFoldersService';
import { projectId, publicAnonKey } from '../../../utils/supabase/info';
import { geocodeAddress } from '../../../utils/geocoding';
import type { Constructor } from './mixins';

export const withUtils = <TBase extends Constructor<any>>(Base: TBase) => class Utils extends Base {
  // UTILITY METHODS
  // ============================================================================

  private generateId(): string {
    return crypto.randomUUID();
  }

  /**
   * Get database statistics
   */
  public getStats() {
    return {
      companies: Object.keys(this.db.companies || {}).length,
      users: Object.keys(this.db.users || {}).length,
      projects: Object.keys(this.db.projects || {}).length,
      contractItems: Object.keys(this.db.contractItems || {}).length,
      interimApplications: Object.keys(this.db.interimApplications || {}).length,
      tenders: Object.keys(this.db.tenders || {}).length,
      tasks: Object.keys(this.db.tasks || {}).length,
      notifications: Object.keys(this.db.notifications || {}).length,
      lastUpdated: this.db.lastUpdated,
      version: this.db.version,
    };
  }

  /**
   * Validate data integrity and fix common issues
   * Identifies orphaned projects, missing client references, etc.
   */
  public validateDataIntegrity(): {
    isValid: boolean;
    issues: string[];
    orphanedProjects: string[];
    fixedIssues: string[];
  } {
    const issues: string[] = [];
    const orphanedProjects: string[] = [];
    const fixedIssues: string[] = [];

    // Get all client companies
    const clientCompanies = (Object.values(this.db.companies) as Company[]).filter((c: Company) => c.type === 'client');
    const clientIds = new Set(clientCompanies.map((c: Company) => c.id));

    // Check all projects for valid client references
    const projects = Object.values(this.db.projects) as Project[];
    projects.forEach((project: Project) => {
      // Check if project references a valid client
      if (!clientIds.has(project.clientId)) {
        issues.push(`Project "${project.name}" (${project.id}) references non-existent or non-client company: ${project.clientId}`);
        orphanedProjects.push(project.id);
      }
    });

    // Check contract items for valid project references
    const projectIds = new Set(projects.map((p: Project) => p.id));
    const contractItems = Object.values(this.db.contractItems) as ContractItem[];
    contractItems.forEach((item: ContractItem) => {
      if (!projectIds.has(item.projectId)) {
        issues.push(`Contract item "${item.title}" (${item.id}) references non-existent project: ${item.projectId}`);
      }
    });

    // Check tasks for valid project references
    const tasks = Object.values(this.db.tasks) as Task[];
    tasks.forEach((task: Task) => {
      if (task.project_id && !projectIds.has(task.project_id)) {
        issues.push(`Task "${task.title}" (${task.id}) references non-existent project: ${task.project_id}`);
      }
    });

    return {
      isValid: issues.length === 0,
      issues,
      orphanedProjects,
      fixedIssues,
    };
  }

  /**
   * Clean up orphaned data (use with caution!)
   * This will remove projects that don't reference valid client companies
   */
  public cleanupOrphanedData(): { 
    deletedProjects: number;
    deletedContractItems: number;
    deletedTasks: number;
  } {
    const clientCompanies = (Object.values(this.db.companies) as Company[]).filter((c: Company) => c.type === 'client');
    const clientIds = new Set(clientCompanies.map((c: Company) => c.id));

    let deletedProjects = 0;
    let deletedContractItems = 0;
    let deletedTasks = 0;

    // Remove projects with invalid client references
    const projects = Object.values(this.db.projects) as Project[];
    const validProjectIds = new Set<string>();

    projects.forEach((project: Project) => {
      if (clientIds.has(project.clientId)) {
        validProjectIds.add(project.id);
      } else {
        console.warn(`Deleting orphaned project: ${project.name} (${project.id}) - invalid clientId: ${project.clientId}`);
        delete this.db.projects[project.id];
        deletedProjects++;
      }
    });

    // Remove contract items for non-existent projects
    const contractItems = Object.values(this.db.contractItems) as ContractItem[];
    contractItems.forEach((item: ContractItem) => {
      if (!validProjectIds.has(item.projectId)) {
        console.warn(`Deleting orphaned contract item: ${item.title} (${item.id})`);
        delete this.db.contractItems[item.id];
        deletedContractItems++;
      }
    });

    // Remove tasks for non-existent projects
    const tasks = Object.values(this.db.tasks) as Task[];
    tasks.forEach((task: Task) => {
      if (task.project_id && !validProjectIds.has(task.project_id)) {
        console.warn(`Deleting orphaned task: ${task.title} (${task.id})`);
        delete this.db.tasks[task.id];
        deletedTasks++;
      }
    });

    if (deletedProjects > 0 || deletedContractItems > 0 || deletedTasks > 0) {
      this.saveToStorage();
      this.notifyListeners();
      console.log(`✅ Cleanup complete: ${deletedProjects} projects, ${deletedContractItems} contract items, ${deletedTasks} tasks deleted`);
    }

    return { deletedProjects, deletedContractItems, deletedTasks };
  }

  // ============================================================================
};